import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Eye, MonitorPlay } from "lucide-react";
import { ImageUpload } from "@/components/admin/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });

const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold disabled:opacity-50";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3.5 py-2 text-xs";

export const OnboardingVideoSettings = () => {
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    req("get", "/admin/onboarding-video")
      .then((r) => {
        setForm(r.data);
        setPreview(r.data.embed_url || "");
      })
      .catch(() => toast.error("Could not load onboarding settings"));
  }, []);

  const doPreview = async () => {
    try {
      const { data } = await req("post", "/admin/training/preview", {
        video_source: form.video_source,
        video_link: form.video_link,
      });
      setPreview(data.embed_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid video link");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await req("put", "/admin/onboarding-video", {
        title: form.title,
        description: form.description,
        video_source: form.video_source,
        video_link: form.video_link,
        cover: form.cover,
        status: form.status,
      });
      setForm(data);
      setPreview(data.embed_url || "");
      toast.success("Onboarding video saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  if (!form) return null;

  return (
    <div
      data-testid="onboarding-video-settings"
      className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <MonitorPlay className="h-4 w-4 text-[#06B6D4]" />
        <h3 className="font-display text-base font-semibold tracking-tight">Onboarding video pop-up</h3>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        Shown to every student the first time they open their dashboard, until they tick "Don't show
        me again".
      </p>

      <input
        className={input}
        value={form.title || ""}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Video title"
        data-testid="onb-title"
      />
      <textarea
        rows={2}
        className={input}
        value={form.description || ""}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Short description"
        data-testid="onb-description"
      />
      <div className="grid sm:grid-cols-[150px_1fr] gap-2">
        <select
          className={input}
          value={form.video_source}
          onChange={(e) => setForm({ ...form, video_source: e.target.value })}
          data-testid="onb-source"
        >
          <option value="youtube">YouTube</option>
          <option value="drive">Google Drive</option>
        </select>
        <input
          className={input}
          value={form.video_link || ""}
          onChange={(e) => setForm({ ...form, video_link: e.target.value })}
          placeholder={
            form.video_source === "youtube"
              ? "https://youtube.com/watch?v=…"
              : "https://drive.google.com/file/d/…/view"
          }
          data-testid="onb-link"
        />
      </div>

      <ImageUpload
        value={form.cover}
        onChange={(url) => setForm({ ...form, cover: url })}
        folder="covers"
        label="Cover photo for the pop-up"
        testid="onb-cover-upload"
      />

      {preview && (
        <div className="aspect-video w-full max-w-md rounded-xl overflow-hidden border border-slate-200 dark:border-white/10">
          <iframe src={preview} title="Onboarding preview" allowFullScreen className="h-full w-full" />
        </div>
      )}

      <select
        className={input}
        value={form.status}
        onChange={(e) => setForm({ ...form, status: e.target.value })}
        data-testid="onb-status"
      >
        <option value="enabled">Enabled — show to students</option>
        <option value="disabled">Disabled</option>
      </select>

      <div className="flex items-center gap-2">
        <button type="button" onClick={doPreview} className={btnGhost} data-testid="onb-preview">
          <Eye className="h-3 w-3" /> Preview video
        </button>
        <button type="button" onClick={save} disabled={busy} className={btnDark} data-testid="onb-save">
          <Check className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
};
